import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Brain, Check, Eye, RotateCcw } from 'lucide-react';
import { playCorrect, playWrong, playClick } from '../../utils/sounds';
import { PrecisionHUD, PrecisionStats, TrialStrip } from './PrecisionUI';
import { createRecallSequence, getMemoryRules, nextMemorySpan, recallAnswer, recallRuleForRound, RECALL_NAMES, summarizeRecall } from './memoryRules';
import type { PrecisionProps, PrecisionResult, TrialRecord } from './types';

type Phase = 'observe' | 'retain' | 'recall' | 'feedback';
const SYMBOLS = ['●', '◆', '▲', '■', '✦', '✚', '⬟', '◎', '▰'];

export default function PrecisionMemory({ game, mode, tier, onComplete, onFail }: PrecisionProps) {
  const rules = useMemo(() => getMemoryRules(game, mode, tier), [game, mode, tier]);
  const [round, setRound] = useState(0);
  const [span, setSpan] = useState(rules.startSpan);
  const [sequence, setSequence] = useState(() => createRecallSequence(rules.startSpan, rules.grid));
  const [phase, setPhase] = useState<Phase>('observe');
  const [lit, setLit] = useState(-1);
  const [displayStep, setDisplayStep] = useState(0);
  const [entered, setEntered] = useState<number[]>([]);
  const [trials, setTrials] = useState<TrialRecord[]>([]);
  const [remaining, setRemaining] = useState(1);
  const [feedback, setFeedback] = useState('');
  const [replayUses, setReplayUses] = useState(0);
  const [presentation, setPresentation] = useState(0);
  const input = useRef<number[]>([]);
  const records = useRef<TrialRecord[]>([]);
  const history = useRef<NonNullable<PrecisionResult['memoryReplay']>['rounds']>([]);
  const assisted = useRef(false);
  const replays = useRef(0);
  const phaseRef = useRef<Phase>('observe');
  const deadline = useRef(0);
  const started = useRef(0);
  const sessionStart = useRef(performance.now());
  const finished = useRef(false);
  const lastTap = useRef(-Infinity);
  const nextTimer = useRef<ReturnType<typeof setTimeout>>();
  const resolveRef = useRef<(timedOut: boolean) => void>(() => {});
  const callbacks = useRef({ onComplete, onFail });
  callbacks.current = { onComplete, onFail };
  const rule = recallRuleForRound(round, rules.level, game.mechanic === 'grid');
  const expected = recallAnswer(sequence, rule, rules.grid);
  const stats = summarizeRecall(trials, rules.rounds, replayUses);
  const inputWindow = 1600 + span * rules.inputPerItemMs;

  resolveRef.current = timedOut => {
    if (finished.current || phaseRef.current !== 'recall') return;
    phaseRef.current = 'feedback';
    setPhase('feedback');
    const success = !timedOut && input.current.length === expected.length && input.current.every((n, i) => n === expected[i]);
    const wrongAt = expected.findIndex((n, i) => input.current[i] !== n);
    const label = timedOut ? `超时 · 已完成 ${input.current.length}/${span}` : success ? (assisted.current ? '辅助完成' : '独立重现') : `第 ${wrongAt + 1} 步记错`;
    const trial: TrialRecord = { index: round, outcome: timedOut ? 'timeout' : success ? assisted.current ? 'good' : 'perfect' : 'wrong', responseMs: null, label };
    const next = [...records.current, trial];
    records.current = next;
    history.current.push({ rule: RECALL_NAMES[rule], shown: sequence, expected, entered: [...input.current], assisted: assisted.current });
    setTrials(next);
    setFeedback(label);
    success ? playCorrect() : playWrong();
    const summary = summarizeRecall(next, rules.rounds, replays.current);
    const failed = summary.mistakes >= rules.mistakeLimit;
    nextTimer.current = setTimeout(() => {
      if (finished.current) return;
      if (failed || next.length >= rules.rounds) {
        finished.current = true;
        const independent = history.current.filter((h, i) => next[i].outcome === 'perfect' && !h.assisted);
        const bestSpan = Math.max(0, ...independent.map(h => h.expected.length));
        const precision: PrecisionResult = {
          ...summary, version: 1, tier: rules.level, trials: next, durationSeconds: (performance.now() - sessionStart.current) / 1000,
          failureReason: failed ? `已达到 ${rules.mistakeLimit} 次失误。先把序列分成两三步的小组，再逐组重现。` : undefined,
          metrics: [{ label: '独立记忆跨度', value: `${bestSpan} 步` }, { label: '独立完成', value: `${independent.length}/${rules.rounds} 轮` }, { label: '使用重看', value: `${replays.current} 次` }],
          insights: [summary.mistakes ? '先确认顺序规则，再把长序列分组记忆；速度不影响本项精准奖励。' : '独立重现很稳定，可尝试更长序列或倒序规则。', `重看每次扣 5 分，辅助完成不获精准奖励；准确率按整轮重现统计。`],
          memoryReplay: { grid: rules.grid, rounds: history.current },
        };
        const replay = { mechanic: game.mechanic, precision };
        failed ? callbacks.current.onFail(replay) : callbacks.current.onComplete(summary.score, replay);
      } else {
        const newSpan = nextMemorySpan(span, success, rules.maxSpan);
        input.current = [];
        assisted.current = false;
        phaseRef.current = 'observe';
        setEntered([]); setLit(-1); setDisplayStep(0); setRemaining(1);
        setSpan(newSpan); setSequence(createRecallSequence(newSpan, rules.grid));
        setPhase('observe'); setRound(round + 1);
      }
    }, 1400);
  };

  // Presentation time begins after commit. Every symbol remains visible for a full slot.
  useLayoutEffect(() => {
    finished.current = false;
    phaseRef.current = 'observe';
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const show = (index: number) => {
      if (cancelled) return;
      setLit(sequence[index]); setDisplayStep(index + 1);
      timer = setTimeout(() => {
        setLit(-1);
        timer = setTimeout(() => {
          if (index + 1 < sequence.length) show(index + 1);
          else {
            phaseRef.current = 'retain'; setPhase('retain');
            timer = setTimeout(() => {
              started.current = performance.now(); deadline.current = started.current + inputWindow;
              phaseRef.current = 'recall'; setPhase('recall'); setRemaining(1);
            }, rules.retentionMs);
          }
        }, rules.gapMs);
      }, rules.flashMs);
    };
    timer = setTimeout(() => show(0), 650);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [round, presentation, sequence, inputWindow, rules]);

  useEffect(() => {
    if (phase !== 'recall') return;
    const timer = setInterval(() => {
      const left = deadline.current - performance.now();
      setRemaining(Math.max(0, left / inputWindow));
      if (left <= 0) resolveRef.current(true);
    }, 40);
    return () => clearInterval(timer);
  }, [phase, inputWindow]);
  useEffect(() => { finished.current = false; return () => { finished.current = true; clearTimeout(nextTimer.current); }; }, []);

  const choose = (cell: number) => {
    if (phaseRef.current !== 'recall' || finished.current) return;
    const now = performance.now();
    if (now >= deadline.current) { resolveRef.current(true); return; }
    if (now - lastTap.current < 100) return;
    lastTap.current = now;
    input.current = [...input.current, cell]; setEntered(input.current);
    if (cell !== expected[input.current.length - 1] || input.current.length === expected.length) resolveRef.current(false);
    else playClick();
  };
  const replay = () => {
    if (phaseRef.current !== 'recall' || input.current.length || assisted.current || replays.current >= rules.replayBudget || finished.current) return;
    if (performance.now() >= deadline.current) { resolveRef.current(true); return; }
    replays.current++; assisted.current = true; phaseRef.current = 'observe';
    setReplayUses(replays.current); setPhase('observe'); setDisplayStep(0); setPresentation(v => v + 1);
  };

  return <div className="precision-game memory-game">
    <PrecisionHUD round={round + 1} total={rules.rounds} combo={stats.combo} mistakes={stats.mistakes} limit={rules.mistakeLimit} progress={phase === 'recall' ? remaining : 1} label={game.mechanic === 'grid' ? '空间记忆实验' : '序列记忆实验'} />
    <div className="memory-phases" aria-label="本轮阶段">{[['observe', '观察'], ['retain', '保持'], ['recall', '重现']].map(([key, name], i) => <span key={key} className={phase === key ? 'active' : ''}><b>{`0${i + 1}`}</b>{name}</span>)}</div>
    <div className="precision-rule memory-rule" aria-live="polite"><span>{phase === 'observe' ? '只看不点 · 记住闪烁位置' : phase === 'retain' ? '保持记忆 · 忽略遮罩' : phase === 'recall' ? '现在轮到你' : '本轮判定'}</span><strong>{phase === 'feedback' ? feedback : RECALL_NAMES[rule]}</strong><small>{span} 步序列 · {phase === 'observe' ? `正在展示 ${displayStep}/${span}` : phase === 'recall' ? `已输入 ${entered.length}/${span} · 剩余 ${(remaining * inputWindow / 1000).toFixed(1)} 秒` : rule === 'mirror' ? '顺序不变，每个位置左右对换' : rule === 'reverse' ? '从最后一步开始回忆' : '按看到的先后顺序重现'}</small></div>
    <div className={`precision-grid memory-board ${phase === 'retain' && rules.level > 0 ? 'masked' : ''}`} style={{ gridTemplateColumns: `repeat(${rules.grid}, minmax(0,1fr))` }} aria-label="记忆方格">
      {Array.from({ length: rules.grid * rules.grid }, (_, cell) => <button key={cell} className={`precision-cell memory-cell ${phase === 'observe' && lit === cell ? 'lit' : ''} ${phase === 'recall' && entered.at(-1) === cell ? 'selected' : ''}`} disabled={phase !== 'recall'} aria-label={`第${Math.floor(cell / rules.grid) + 1}行第${cell % rules.grid + 1}列`} onClick={() => choose(cell)}>
        {game.mechanic === 'sequence' ? <span className="memory-symbol">{SYMBOLS[cell]}</span> : <span className="memory-position">{cell + 1}</span>}
        {phase === 'observe' && lit === cell && <span className="memory-step">{displayStep}</span>}
      </button>)}
      {phase === 'retain' && rules.level > 0 && <div className="memory-mask"><Brain size={30} /><span>把顺序留在脑海里</span></div>}
    </div>
    <div className="memory-slots" aria-label={`已输入 ${entered.length} 步`}>{Array.from({ length: span }, (_, i) => <span key={i} className={i < entered.length ? 'filled' : ''}>{i < entered.length ? <Check size={12} /> : i + 1}</span>)}</div>
    {phase === 'feedback' && <p className="memory-answer">正确位置编号：{expected.map(n => n + 1).join(' → ')}</p>}
    {rules.replayBudget > 0 && <button className="memory-replay" disabled={phase !== 'recall' || entered.length > 0 || assisted.current || replayUses >= rules.replayBudget} onClick={replay}><RotateCcw size={13} /> 重看本轮 <span>剩余 {rules.replayBudget - replayUses} 次 · 扣 5 分</span></button>}
    <TrialStrip trials={trials} total={rules.rounds} />
    <PrecisionStats stats={[{ label: '当前跨度', value: `${span} 步` }, { label: '本局评分', value: stats.score }, { label: '独立重现', value: `${stats.perfectCount} 轮` }]} />
    <p className="precision-note"><Eye size={12} className="inline mr-1" />正确后增加一步，失误后回退一步。规则在展示前公布；观察时不可输入。以完整记住为目标，不奖励抢点。</p>
  </div>;
}
